import { PENDING_BUSINESS_ID, Tier } from '@mondaygpt/constants'
import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { BusinessBillingService } from 'openapi'
import { RootState } from 'types'
import { logout } from '../../authentication/slices/authentication-view.slice'
import { selectBusinessId } from '../../common/selectors/authentication-view.selector'

export interface CreditBalance {
  subscriptionCredits: number
  topUpCredits: number
  totalCredits: number
}

export interface BusinessEntitlement {
  tier: string
  product?: {
    monthlyCredits: number
    maxSeats: number
    maxBrandKits: number
    period: string
  }
  /**
   * ISO timestamp of the active subscription's next renewal (server maps it
   * from the RevenueCat subscription's period end). Absent on the free tier or
   * when the vendor didn't supply one. Drives "Renews {date}" and the concrete
   * "Credits reset on the Nth" line on the billing page.
   */
  renewsAt?: string
}

export interface CreditBalanceState {
  balance: CreditBalance | null
  entitlement: BusinessEntitlement | null
  /**
   * Server-computed: the business has an actively-billing subscription that
   * blocks deletion (Active/Grace Period, external vendor). Cancelled-but-paid
   * and tester/admin (Stripe) grants are false. Drives the up-front
   * cancel-first dialog on business deletion.
   */
  hasBillableSubscription: boolean
  loading: boolean
  error?: string
}

const initialState: CreditBalanceState = {
  balance: null,
  entitlement: null,
  hasBillableSubscription: false,
  loading: true,
}

export const fetchBusinessBilling = createAsyncThunk(
  'creditBalance/fetchBilling',
  async (businessId: string, { rejectWithValue }) => {
    try {
      return await BusinessBillingService.businessBillingControllerGetBillingV1(businessId)
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch billing')
    }
  },
  {
    condition: businessId => businessId !== PENDING_BUSINESS_ID,
  }
)

const creditBalanceSlice = createSlice({
  name: 'creditBalance',
  initialState,
  reducers: {
    updateCreditBalance: (state, action: PayloadAction<CreditBalance>) => {
      state.balance = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(logout, () => initialState)

    builder
      .addCase(fetchBusinessBilling.pending, state => {
        state.loading = true
        state.error = undefined
      })
      .addCase(fetchBusinessBilling.fulfilled, (state, action) => {
        state.loading = false
        state.balance = action.payload.credits
        state.entitlement = action.payload.entitlement
        state.hasBillableSubscription = action.payload.hasBillableSubscription ?? false
        state.error = undefined
      })
      .addCase(fetchBusinessBilling.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  },
})

export const { updateCreditBalance } = creditBalanceSlice.actions
export const creditBalanceReducer = creditBalanceSlice.reducer

// Selectors
export const selectCreditBalanceState = (state: RootState) => state.creditBalance

export const selectCreditBalance = createSelector(selectCreditBalanceState, state => state.balance)

export const selectTotalCredits = createSelector(
  selectCreditBalance,
  balance => balance?.totalCredits ?? 0
)

export const selectBusinessEntitlement = createSelector(
  selectCreditBalanceState,
  state => state.entitlement
)

export const selectHasActiveSubscription = createSelector(
  selectBusinessEntitlement,
  entitlement => entitlement?.tier !== Tier.Free && entitlement?.tier !== undefined
)

/**
 * Server-authoritative flag: the current business has an actively-billing
 * subscription that blocks deletion. Unlike selectHasActiveSubscription
 * (tier-based, true for cancelled-but-paid too), this is false once the user
 * has cancelled, so the client can gate deletion exactly like the server does.
 */
export const selectHasBillableSubscription = createSelector(
  selectCreditBalanceState,
  state => state.hasBillableSubscription
)

export const selectCreditBalanceLoading = createSelector(
  selectCreditBalanceState,
  state => state.loading
)

export const selectCreditBalanceError = createSelector(
  selectCreditBalanceState,
  state => state.error
)

/** Single source of truth for "the user is fully out of credits". Loading- and
 *  error-aware so surfaces don't flash the out-of-credits state before the
 *  balance has loaded — `selectTotalCredits` coerces a null balance to 0, which
 *  a bare `=== 0` check can't distinguish from a genuine empty balance. */
export const selectIsOutOfCredits = createSelector(
  [selectCreditBalance, selectCreditBalanceLoading, selectCreditBalanceError],
  (balance, loading, error) => !loading && !error && !!balance && balance.totalCredits === 0
)

export const selectShouldShowUpgrade = createSelector(
  [selectBusinessEntitlement, selectCreditBalanceLoading, selectBusinessId],
  (entitlement, loading, businessId) => {
    if (loading) return false
    if (!businessId) return true
    if (!entitlement) return true
    return entitlement.tier === Tier.Free
  }
)
